import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { EMPTY, Observable, finalize, take, tap, throwError } from 'rxjs';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import { LivenessSession } from '@shared/models/face-liveness.model';
import {
  ApiErrorBody,
  FaceEnrollmentResult,
  ForgotPasswordRequest,
  ForgotPasswordResult,
  LoginCredentials,
  LoginResponse,
  LoginResult,
  ResetPasswordRequest,
  ResetPasswordResult,
  isLoginChallenge,
} from '../models/auth.model';
import { AuthApiService } from './auth-api.service';
import { AuthStateService } from './auth-state.service';

@Injectable({ providedIn: 'root' })
export class AuthFacadeService {
  private readonly api = inject(AuthApiService);
  private readonly state = inject(AuthStateService);
  private readonly appStateReset = inject(AppStateResetService);

  /**
   * Which credential the face step answers: the password step's challenge, or the
   * e-mail of a direct face login (no password).
   *
   * `null` once it is spent, and also on a reload - both live in memory only. The
   * screen needs it because the two doors read the API's refusal differently: on
   * the direct one a refusal the API does not count means the e-mail and the face
   * were never going to match, and there is nothing to try again.
   */
  readonly faceStep = computed<'challenge' | 'email' | null>(() => {
    if (this.state.challengeToken()) {
      return 'challenge';
    }

    return this.state.faceLoginEmail() ? 'email' : null;
  });

  readonly vm = computed(() => ({
    isLoading: this.state.isLoading(),
    isForgotPasswordLoading: this.state.isForgotPasswordLoading(),
    isResetPasswordLoading: this.state.isResetPasswordLoading(),
    isAuthenticated: this.state.isAuthenticated(),
    error: this.state.error(),
    user: this.state.user(),
  }));

  /**
   * Authenticates the user and keeps the auth state in sync.
   * Returns the stream so callers can drive navigation/feedback on success/error.
   */
  login(credentials: LoginCredentials): Observable<LoginResponse> {
    this.state.setLoading();

    return this.api.login(credentials).pipe(
      take(1),
      tap({
        // An account with a face enrolled gets a challenge, not a session: no
        // token is stored until the face step answers it.
        next: (response) =>
          isLoginChallenge(response)
            ? this.state.setLoginChallenge(response.challenge_token)
            : this.state.setAuthenticated(response),
        error: (error: unknown) => this.state.setError(this.extractErrorMessage(error)),
      }),
    );
  }

  /**
   * Verifies that the typed email belongs to an existing account.
   * Returns the stream so the page can flip its view on success/error.
   */
  forgotPassword(payload: ForgotPasswordRequest): Observable<ForgotPasswordResult> {
    this.state.setForgotPasswordLoading(true);

    return this.api.forgotPassword(payload).pipe(
      take(1),
      finalize(() => this.state.setForgotPasswordLoading(false)),
    );
  }

  /**
   * Sets a new password for the account and exposes the stream so the page can
   * drive feedback/navigation on success/error.
   */
  resetPassword(payload: ResetPasswordRequest): Observable<ResetPasswordResult> {
    this.state.setResetPasswordLoading(true);

    return this.api.resetPassword(payload).pipe(
      take(1),
      finalize(() => this.state.setResetPasswordLoading(false)),
    );
  }

  /** Opens a liveness session for the face enrolment. Each attempt needs its own. */
  openFaceEnrollmentSession(): Observable<LivenessSession> {
    return this.api.openFaceEnrollmentSession().pipe(take(1));
  }

  /**
   * Enrolls the face from a finished liveness session and stores the returned
   * user, which now carries `face: true`. The token does not change.
   */
  enrollFace(livenessSessionId: string): Observable<FaceEnrollmentResult> {
    return this.api.enrollFace(livenessSessionId).pipe(
      take(1),
      tap((result) => this.state.updateUser(result.user)),
    );
  }

  /**
   * Starts the direct face login (email + face, no password): the face step that
   * follows runs on this email instead of a password challenge.
   */
  startFaceLogin(email: string): void {
    this.state.setFaceLoginEmail(email);
  }

  /**
   * Opens a liveness session for the login's face step. Two doors lead there:
   * the password step's challenge (2FA) or the email of a direct face login.
   *
   * Both live in memory only, so a reload of the face step has neither. That
   * fails as the 401 an expired challenge gets from the API, and the page
   * handles both the same way: back to the login.
   */
  openLoginFaceSession(): Observable<LivenessSession> {
    const challengeToken = this.state.challengeToken();
    if (challengeToken) {
      return this.api.openLoginFaceSession(challengeToken).pipe(take(1));
    }
    const email = this.state.faceLoginEmail();
    if (email) {
      return this.api.openFaceLoginSession(email).pipe(take(1));
    }
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  }

  /** Answers the face step with a finished liveness session and signs the user in. */
  verifyLoginFace(livenessSessionId: string): Observable<LoginResult> {
    const challengeToken = this.state.challengeToken();
    const email = this.state.faceLoginEmail();
    const request = challengeToken
      ? this.api.verifyLoginFace(challengeToken, livenessSessionId)
      : email
        ? this.api.faceLogin(email, livenessSessionId)
        : null;
    if (!request) {
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }
    return request.pipe(
      take(1),
      tap((result) => this.state.setAuthenticated(result)),
    );
  }

  /**
   * Revokes the session on the API (when a token is present) and always clears
   * the whole application state — the auth state among it, since it registers
   * itself like every other feature — so the client never stays logged in if the
   * call fails, and nothing the previous user loaded outlives their session.
   */
  logout(): Observable<void> {
    const token = this.state.token();
    const request = token ? this.api.logout() : EMPTY;

    return request.pipe(
      take(1),
      finalize(() => this.appStateReset.resetAll()),
    );
  }

  /**
   * Pulls the human-readable message out of the API error body, falling back to
   * a generic key when the response doesn't carry one.
   */
  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiErrorBody | null;
      if (body?.message) {
        return body.message;
      }
    }

    return 'AUTH.LOGIN_ERROR';
  }
}
